import { DatePicker } from 'antd';
import type { GetProps } from 'antd';
import { forwardRef } from 'react';
import {
  controlSizeForContext,
  CONTROL_CLASS,
} from '@/theme/controlSizing';
import type { ControlContext } from '@/theme/controlSizing';
import { DEFAULT_DATE_FORMAT } from './DatePickerBox';

const { RangePicker } = DatePicker;

type AntdRangePickerProps = GetProps<typeof RangePicker>;

/**
 * Props {@link DateRangePickerBox}.
 */
export interface DateRangePickerBoxProps
  extends Omit<AntdRangePickerProps, 'size'> {
  /** Konteks pemakaian — menentukan density. Default: `form`. */
  context?: ControlContext;
  /** Override paksa size Antd jika perlu. */
  size?: AntdRangePickerProps['size'];
}

/**
 * Date range picker — wrapper `Antd DatePicker.RangePicker`.
 *
 * Sifat sama dengan {@link DatePickerBox} (density via `context`, width 100%,
 * format `DD/MM/YYYY` default, class `booku-control`).
 *
 * Pemakaian:
 * ```tsx
 * <DateRangePickerBox
 *   value={[from, to]}
 *   onChange={(values) => { setFrom(values?.[0]); setTo(values?.[1]); }}
 * />
 * ```
 *
 * Dokumentasi pattern: `.claude/docs/Booku v2/ui-patterns/control-system-pattern.md`
 */
export const DateRangePickerBox = forwardRef<
  React.ComponentRef<typeof RangePicker>,
  DateRangePickerBoxProps
>(function DateRangePickerBox(
  { context = 'form', size, format, style, className, ...rest },
  ref
) {
  const resolvedSize = size ?? controlSizeForContext(context);
  return (
    <RangePicker
      ref={ref}
      size={resolvedSize}
      format={format ?? DEFAULT_DATE_FORMAT}
      className={`${CONTROL_CLASS}${className ? ` ${className}` : ''}`}
      style={{ width: '100%', ...style }}
      {...rest}
    />
  );
});
